import CreativeGridView from '@/Enums/CampaignManagement/CreativeGridView.enum';
import type { Channel } from '@/Enums/Channel.enum';
import type CreativeType from '@/Enums/Creatives/CreativeType.enum';
import type { ILineItemCreative } from '@/Interfaces/CampaignManagement/LineItem.interface';
import type { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import { GridPaginationAndSortFilters } from '@/components/Grid/hooks/useGridApiPaginationAndSort';
import ModalBody from '@/components/Modal/common/ModalBody';
import { CREATIVE_TYPES_FOR_CHANNEL, DEFAULT_CREATIVE_FILTERS } from '@/constants/creativeLibrary';
import { getAdvertiserControlOption } from '@/helpers/advertisers';
import CreativeFormModal, { ICreativeFormModalDefaults } from '@/pages/CreativeLibrary/Modals/CreativeFormModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectActiveCampaignAdvertiser } from '@/store/selectors/campaignManagement.selectors';
import { getCreatives, setFilters } from '@/store/slices/creativeSlice';
import { Modal } from '@mui/material';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, FormEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CreativesTable from '../Table';
import { getColumns } from '../Table/columns';
import CreativesModalButtons from './components/CreativesModalButtons';
import CreativesModalHeader from './components/CreativesModalHeader';
import CreativesModalSearch from './components/CreativesModalSearch';

interface ICreativesModalProps {
  open: boolean;
  handleClose: () => void;
  associatedCreatives: ILineItemCreative[];
  handleAssociateCreatives: (creatives: ICreative[]) => void;
  channel: Channel.CTV | Channel.OLV | Channel.Display | Channel.Audio;
  allowedTypes?: CreativeType[];
  selectionMode?: 'single' | 'multiple';
}

const AssociateCreativesModal = memo(
  ({
    open,
    handleClose,
    associatedCreatives,
    handleAssociateCreatives,
    channel,
    allowedTypes,
    selectionMode = 'multiple',
  }: ICreativesModalProps) => {
    const dispatch = useAppDispatch();
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const allCreatives = useAppSelector(({ creative }) => creative.creatives);
    const loading = useAppSelector(({ creative }) => !!creative.creativesRequestId);
    const filters = useAppSelector(({ creative }) => creative.filters);
    const rowCount = useAppSelector(({ creative }) => creative.total);
    const advertiser = useAppSelector(selectActiveCampaignAdvertiser);

    const availableCreatives = useMemo(() => {
      if (!associatedCreatives?.length) {
        return allCreatives;
      }

      const associatedIds = associatedCreatives.map(({ id }) => id);
      return allCreatives.filter(({ id }) => !associatedIds.includes(id));
    }, [associatedCreatives, allCreatives]);

    const typeOptions = useMemo(
      () => allowedTypes ?? (channel ? CREATIVE_TYPES_FOR_CHANNEL[channel] : []),
      [channel, allowedTypes],
    );

    const allCreativesHaveBeeswaxSeatId = useMemo(() => {
      return availableCreatives.length > 0 && availableCreatives.every(creative => creative.beeswaxId != null);
    }, [availableCreatives]);

    const [selectedTypes, setSelectedTypes] = useState<CreativeType[]>(typeOptions);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedCreativesIds, setSelectedCreativesIds] = useState<GridRowSelectionModel>([]);
    const [isCreativeFormModalOpen, setIsCreativeFormModalOpen] = useState<boolean>(false);
    const [creativeFormModalData, setCreativeFormModalData] = useState<ICreative>();
    const [creativeFormModalDefaults, setCreativeFormModalDefaults] = useState<ICreativeFormModalDefaults>();
    const [isPolling, setIsPolling] = useState<boolean>(false);

    const handleSelectedCreativesIdsChange = useCallback(
      (ids: GridRowSelectionModel) => {
        if (selectionMode === 'single') {
          setSelectedCreativesIds(ids.length ? [ids[ids.length - 1]] : []);
          return;
        }

        setSelectedCreativesIds(ids);
      },
      [selectionMode],
    );

    const startPolling = useCallback(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      setIsPolling(true);
      pollingIntervalRef.current = setInterval(() => {
        dispatch(getCreatives());
      }, 5000);
    }, [dispatch]);

    const stopPolling = useCallback(() => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      setIsPolling(false);
    }, []);

    useEffect(() => {
      if (isPolling && allCreativesHaveBeeswaxSeatId) {
        stopPolling();
      }
    }, [isPolling, allCreativesHaveBeeswaxSeatId, stopPolling]);

    useEffect(() => {
      return () => {
        stopPolling();
      };
    }, [stopPolling]);

    useEffect(() => {
      if (open && typeOptions?.length) {
        dispatch(
          setFilters({
            ...DEFAULT_CREATIVE_FILTERS,
            types: typeOptions,
            ...(advertiser ? { advertisers: [{ value: advertiser.id, label: advertiser.name }] } : {}),
          }),
        );
      }
    }, [open, dispatch, typeOptions, advertiser]);

    const getCreativesData = useCallback(() => dispatch(getCreatives()), [dispatch]);

    useEffect(() => {
      if (open) {
        getCreativesData();
      }
    }, [filters, getCreativesData, open]);

    const handleSearch = useCallback(
      debounce(newSearch => {
        dispatch(setFilters({ search: newSearch }));
      }, 500),
      [dispatch],
    );

    const handleSearchValueChange = useCallback(
      ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setSearchValue(value);
        handleSearch(value);
      },
      [handleSearch],
    );

    const handleTypeChange = useCallback(
      (value: CreativeType) => {
        const updatedSelectedTypes = selectedTypes.includes(value)
          ? selectedTypes.filter(type => type !== value)
          : [...selectedTypes, value];

        setSelectedTypes(updatedSelectedTypes);
        dispatch(setFilters({ types: updatedSelectedTypes }));
      },
      [dispatch, selectedTypes],
    );

    const _handleClose = useCallback(() => {
      setSearchValue('');
      setSelectedTypes(typeOptions);
      setSelectedCreativesIds([]);
      stopPolling();
      handleClose();
    }, [handleClose, typeOptions, stopPolling]);

    const handleCreativeFormModalClose = useCallback(
      (refresh: boolean = false) => {
        setIsCreativeFormModalOpen(false);
        setCreativeFormModalData(undefined);
        setCreativeFormModalDefaults(undefined);
        if (refresh === true) {
          getCreativesData();
          startPolling();
        }
      },
      [getCreativesData, startPolling],
    );

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      handleAssociateCreatives(availableCreatives.filter(({ id }) => selectedCreativesIds.includes(id)));
      _handleClose();
    };

    const handleOpenCreativeFormModal = useCallback(() => {
      if (advertiser) {
        setCreativeFormModalDefaults({
          advertiser: getAdvertiserControlOption(advertiser),
          types: typeOptions,
        });
      }
      setIsCreativeFormModalOpen(true);
    }, [advertiser, selectedTypes]);

    const columns = useMemo(
      () =>
        getColumns({
          view: CreativeGridView.LineItemModal,
          handleViewCreative: creative => {
            setIsCreativeFormModalOpen(true);
            setCreativeFormModalDefaults(undefined);
            setCreativeFormModalData(creative);
          },
        }),
      [],
    );

    const setTableFilters = useCallback(
      (filters: GridPaginationAndSortFilters) => dispatch(setFilters(filters)),
      [dispatch],
    );

    return (
      <>
        <Modal open={open}>
          <ModalBody className='!w-[80%] max-w-[1100px]'>
            <CreativesModalHeader handleClose={_handleClose} />
            <CreativesModalSearch
              searchValue={searchValue}
              typeOptions={typeOptions}
              selectedTypes={selectedTypes}
              handleSearch={handleSearchValueChange}
              handleTypeChange={handleTypeChange}
              onAddCreativeClick={handleOpenCreativeFormModal}
              onRefreshClick={getCreativesData}
            />
            {loading || availableCreatives?.length ? (
              <form className='mt-[15px]' onSubmit={handleSubmit}>
                <CreativesTable
                  loading={loading}
                  tableData={availableCreatives}
                  rowCount={rowCount}
                  columns={columns}
                  checkboxSelection={true}
                  rowSelectionModel={selectedCreativesIds}
                  handleRowSelectionModelChange={handleSelectedCreativesIdsChange}
                  selectedCreativesTypes={selectedTypes}
                  setTableFilters={setTableFilters}
                />
                <CreativesModalButtons
                  handleClose={_handleClose}
                  isSaveDisabled={selectedCreativesIds.length === 0}
                  saveButtonTitle={selectionMode === 'single' ? 'Select Creative' : 'Add Selected Creatives'}
                />
              </form>
            ) : (
              <p className='my-20 text-center text-xl font-semibold'>
                {availableCreatives?.length
                  ? 'No Creatives found.'
                  : 'There are no available Creatives. Please add new ones.'}
              </p>
            )}
          </ModalBody>
        </Modal>
        <CreativeFormModal
          open={isCreativeFormModalOpen}
          handleClose={handleCreativeFormModalClose}
          creativeData={creativeFormModalData}
          defaults={creativeFormModalDefaults}
        />
      </>
    );
  },
);

export default AssociateCreativesModal;
